import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useCallback, useEffect, useRef, useState } from 'react';
import { DEFENDER, nextPhase, type DefenderPhase } from '@sq/shared';
import type { GameResult } from '@sq/game-core';
import { useQuest } from '../lib/store.tsx';
import { clearRun, hasPlayed, loadRun, markPlayed, saveRun, saveSummary, summarize, type SavedRun } from '../lib/run.ts';
import type { RoundLogBody } from '@sq/shared';
import { GamePlayer } from '../components/GamePlayer.tsx';

type Intent = 'start' | 'resume';
/** One mounted game: fixed for its lifetime so autosaves don't remount the board. */
type Launch = { run: SavedRun; startPhase?: DefenderPhase; firstRun: boolean; n: number };

const sumScores = (d: SavedRun['data']) =>
  DEFENDER.phases.reduce((a, p) => a + (typeof d[p.scoreKey] === 'number' ? (d[p.scoreKey] as number) : 0), 0);

/**
 * Hosts the whole three-round run. Entered only from Landing (START or Resume) via router state; a reload lands
 * back on Landing, which offers one-tap Resume from the autosave.
 */
export default function Play() {
  const store = useQuest();
  const { eventSlug } = store;
  const nav = useNavigate();
  const location = useLocation();
  const [intent] = useState<Intent | undefined>(() => (location.state as { intent?: Intent } | null)?.intent);
  const [launch, setLaunch] = useState<Launch | null>(null);
  const [startError, setStartError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const runRef = useRef<SavedRun | null>(null);
  const chain = useRef<Promise<void>>(Promise.resolve());
  const started = useRef(false);
  const finishing = useRef(false);

  const finish = useCallback(
    async (r: GameResult) => {
      if (finishing.current) return;
      finishing.current = true;
      setSaving(true);
      const run = runRef.current;
      // Make sure every round checkpoint reached the server before asking it to verify the win.
      await chain.current;
      await store.finishAttempt(run?.attemptId ?? null, r);
      saveSummary(eventSlug, summarize({ ...(run?.data ?? {}), ...r.summary }));
      clearRun(eventSlug);
      markPlayed();
      nav('/reward', { replace: true, state: { justFinished: true } });
    },
    [eventSlug, nav, store.finishAttempt],
  );

  const mount = useCallback((run: SavedRun, n: number) => {
    runRef.current = run;
    const startPhase = run.completedPhase ? nextPhase(run.completedPhase) ?? undefined : undefined;
    setLaunch({ run, startPhase, firstRun: !startPhase && !hasPlayed(), n });
  }, []);

  const begin = useCallback(
    async (n = 0) => {
      setStartError(null);
      setLaunch(null);
      try {
        const a = await store.startAttempt();
        const run = saveRun(eventSlug, { ...a, accessible: store.settings.accessible, completedPhase: null, data: {} });
        mount(run, n);
      } catch (e) {
        setStartError(e instanceof Error ? e.message : 'Could not start the game.');
      }
    },
    [eventSlug, mount, store.startAttempt, store.settings.accessible],
  );

  useEffect(() => {
    document.title = `${store.event.defenderTitle} · Docker`;
    if (!intent || started.current) return;
    started.current = true;
    // Consume the one-shot intent so a reload goes back through Landing's Resume.
    nav('/play', { replace: true, state: null });
    const saved = loadRun(eventSlug);
    if (intent === 'resume' && saved) {
      runRef.current = saved;
      if (saved.completedPhase === DEFENDER.phases[DEFENDER.phases.length - 1].id) {
        // Every phase was played; only the completion report was lost. Report it now.
        void finish({ outcome: 'won', score: sumScores(saved.data), stars: 3, reason: 'Resumed after the last round.', summary: saved.data });
        return;
      }
      mount(saved, 0);
      return;
    }
    if (saved) store.abandonAttempt(saved.attemptId);
    clearRun(eventSlug);
    void begin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onPhase = useCallback(
    (phase: string, data: Record<string, number | string | boolean>, log?: unknown) => {
      const run = runRef.current;
      if (!run || !DEFENDER.phases.some((p) => p.id === phase)) return;
      const p = phase as DefenderPhase;
      runRef.current = saveRun(eventSlug, { ...run, completedPhase: p, data: { ...run.data, ...data } });
      markPlayed();
      const roundLog = (log ?? { taps: [], decisions: [], holds: [] }) as RoundLogBody;
      chain.current = chain.current.then(() => store.checkpoint(run.attemptId, p, data, roundLog));
    },
    [eventSlug, store.checkpoint],
  );

  if (!intent) return <Navigate to="/" replace />;
  if (startError) {
    return (
      <div className="game-screen center-screen">
        <p className="fail-reason" role="alert">{startError}</p>
        <button className="btn primary" onClick={() => void begin()}>Try again</button>
        <button className="btn ghost" onClick={() => nav('/')}>Back to start</button>
      </div>
    );
  }
  if (!launch) {
    return (
      <div className="game-screen center-screen">
        <p className="eyebrow" role="status">{saving ? 'Saving your run…' : 'Booting sandbox…'}</p>
      </div>
    );
  }

  return (
    <GamePlayer
      key={`${launch.run.seed}-${launch.n}`}
      gameId={DEFENDER.id}
      title={store.event.defenderTitle}
      seed={launch.run.seed}
      startPhase={launch.startPhase}
      resumeData={launch.startPhase ? launch.run.data : undefined}
      firstRun={launch.firstRun}
      settings={store.settings}
      onSettings={store.setSettings}
      onPhase={onPhase}
      onFinish={(r) => void finish(r)}
      onQuit={() => nav('/')}
      onRestart={() => {
        store.abandonAttempt(runRef.current?.attemptId ?? null);
        clearRun(eventSlug);
        chain.current = Promise.resolve();
        void begin(launch.n + 1);
      }}
    />
  );
}
